export type ApplicationStatus =
  | "draft"
  | "submitted"
  | "clarification"
  | "accepted"
  | "in_progress"
  | "ready"
  | "rejected";

const TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  draft: ["submitted"],
  submitted: ["clarification", "accepted", "rejected"],
  clarification: ["submitted"],
  accepted: ["clarification", "in_progress", "rejected"],
  in_progress: ["clarification", "ready", "rejected"],
  ready: [],
  rejected: [],
};

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Черновик",
  submitted: "Отправлена",
  clarification: "Нужны уточнения",
  accepted: "Принята",
  in_progress: "В работе",
  ready: "Готово",
  rejected: "Не принята",
};

export function canTransition(from: ApplicationStatus, to: ApplicationStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

export function isTerminalApplication(status: ApplicationStatus): boolean {
  return status === "ready" || status === "rejected";
}

export function sellerMayEdit(status: ApplicationStatus): boolean {
  return status === "draft" || status === "clarification";
}

/** Eleven UX §4.13 brief sections. Required submit fields stay the spec minimum. */
export interface ApplicationBrief {
  aboutYou: string;
  audience: string;
  forWhom: string;
  problem: string;
  measurableResult: string;
  materials: string;
  courseOutline: string;
  volume: string;
  sampleTask: string;
  sampleKey: string;
  mechanics: string;
  visual: string;
  checking: string;
  typicalErrors: string;
  media: string;
  pricing: string;
  saleModel: string;
  timeline: string;
  extraTerms: string;
  rightsDeclaration: boolean;
}

export const APPLICATION_SECTIONS: Array<{
  id: number;
  title: string;
  fields: Array<{ key: Exclude<keyof ApplicationBrief, "rightsDeclaration">; label: string }>;
}> = [
  {
    id: 1,
    title: "О вас и вашей аудитории",
    fields: [
      { key: "aboutYou", label: "О вас" },
      { key: "audience", label: "Аудитория" },
    ],
  },
  {
    id: 2,
    title: "Для кого тренажёр",
    fields: [{ key: "forWhom", label: "Возраст, уровень и контекст" }],
  },
  {
    id: 3,
    title: "Какую учебную проблему решаем",
    fields: [
      { key: "problem", label: "Проблема" },
      { key: "measurableResult", label: "Измеримый результат" },
    ],
  },
  {
    id: 4,
    title: "Исходные материалы и права",
    fields: [{ key: "materials", label: "Материалы и права" }],
  },
  {
    id: 5,
    title: "Программа и объём",
    fields: [
      { key: "courseOutline", label: "Программа" },
      { key: "volume", label: "Объём" },
    ],
  },
  {
    id: 6,
    title: "Примеры заданий",
    fields: [
      { key: "sampleTask", label: "Пример задания" },
      { key: "sampleKey", label: "Ключ и обратная связь" },
    ],
  },
  {
    id: 7,
    title: "Механика и визуальный характер",
    fields: [
      { key: "mechanics", label: "Механика" },
      { key: "visual", label: "Визуальный характер" },
    ],
  },
  {
    id: 8,
    title: "Проверка и типичные ошибки",
    fields: [
      { key: "checking", label: "Как проверяем" },
      { key: "typicalErrors", label: "Типичные ошибки" },
    ],
  },
  {
    id: 9,
    title: "Аудио, видео и изображения",
    fields: [{ key: "media", label: "Медиа" }],
  },
  {
    id: 10,
    title: "Цена и модель продажи",
    fields: [
      { key: "pricing", label: "Цена" },
      { key: "saleModel", label: "Модель продажи" },
    ],
  },
  {
    id: 11,
    title: "Сроки и дополнительные условия",
    fields: [
      { key: "timeline", label: "Сроки" },
      { key: "extraTerms", label: "Дополнительные условия" },
    ],
  },
];

export function emptyBrief(): ApplicationBrief {
  return {
    aboutYou: "",
    audience: "",
    forWhom: "",
    problem: "",
    measurableResult: "",
    materials: "",
    courseOutline: "",
    volume: "",
    sampleTask: "",
    sampleKey: "",
    mechanics: "",
    visual: "",
    checking: "",
    typicalErrors: "",
    media: "",
    pricing: "",
    saleModel: "",
    timeline: "",
    extraTerms: "",
    rightsDeclaration: false,
  };
}

export function normalizeBrief(raw: unknown): ApplicationBrief {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const base = emptyBrief();
  for (const key of Object.keys(base) as Array<keyof ApplicationBrief>) {
    if (key === "rightsDeclaration") {
      base.rightsDeclaration = Boolean(src.rightsDeclaration);
      continue;
    }
    const value = src[key];
    if (typeof value === "string") base[key] = value;
  }
  return base;
}

export function canSubmit(brief: ApplicationBrief): boolean {
  const n = normalizeBrief(brief);
  return Boolean(
    n.audience.trim() &&
      n.measurableResult.trim() &&
      n.courseOutline.trim() &&
      n.sampleTask.trim() &&
      n.sampleKey.trim() &&
      n.rightsDeclaration,
  );
}
